import type { Entry } from "contentful";
import { Navigation } from "swiper/modules";

import { Carousel } from "~/components/ui/carousel";
import { H2 } from "~/components/ui/text";
import {
  CAROUSEL_TILE_RIGHT_MARGIN_DESKTOP,
  CAROUSEL_TILE_RIGHT_MARGIN_MOBILE,
  CAROUSEL_TILE_RIGHT_MARGIN_TABLET,
  SCREENS,
} from "~/lib/constants";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";

import type {
  TypeImageWithCtaSkeleton,
  TypeImageWithHyperlinkSkeleton,
  TypeMediaWithLinkSkeleton,
} from "../compiled";
import type {
  CarouselContent,
  ImageWithCTAContent,
  ImageWithHyperlinkContent,
} from "../types";
import { getMaxWidthClass } from "../util";
import { MediaWithLinkContent } from "./home-page-hero";
import { ContentfulImageWithCTA } from "./image-with-cta";
import { ContentfulImageWithHyperlink } from "./image-with-hyperlink";
import { ContentfulLink } from "./link";

const sm = Number.parseInt(SCREENS.sm.slice(0, -2));
const md = Number.parseInt(SCREENS.md.slice(0, -2));
const lg = Number.parseInt(SCREENS.lg.slice(0, -2));

type props = SerializeFrom<
  Entry<
    | TypeImageWithCtaSkeleton
    | TypeImageWithHyperlinkSkeleton
    | TypeMediaWithLinkSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >
>;

const getTile = (
  image: props,
  key: number,
  numberOfColumns: [number, number, number],
) => {
  if (image.sys.contentType.sys.id === "imageWithCta") {
    return (
      <ContentfulImageWithCTA
        key={key}
        content={image as ImageWithCTAContent}
        className="rounded-lg"
        numberOfColumns={numberOfColumns}
      />
    );
  } else if (image.sys.contentType.sys.id === "imageWithHyperlink") {
    return (
      <ContentfulImageWithHyperlink
        key={key}
        content={image as ImageWithHyperlinkContent}
        className="rounded-lg"
        numberOfColumns={numberOfColumns}
      />
    );
  } else if (image.sys.contentType.sys.id === "mediaWithLink") {
    return (
      <MediaWithLinkContent
        key={key}
        content={image as any} // Cast as needed for MediaWithLink
      />
    );
  }
  return null;
};

export const ContentfulCarousel = ({
  carouselContent,
  className = "",
}: {
  carouselContent: CarouselContent;
  className?: string;
}) => {
  const tiles = carouselContent.fields.content;

  // Get the Tailwind class for maxWidth
  const maxWidthClass = getMaxWidthClass(carouselContent.fields.maxWidth);

  // Prepare the numberOfColumns array
  const numberOfColumns: [number, number, number] = [
    carouselContent.fields.numberOfColumnsMobile || 1,
    carouselContent.fields.numberOfColumnsTablet || 1,
    carouselContent.fields.numberOfColumnsDesktop || 1,
  ];

  return (
    <div className={`${className} ${maxWidthClass} mx-auto`}>
      <div className="flex flex-row items-center justify-between">
        <H2 className="leading-lg text-brand-primary-black text-[26px] font-semibold uppercase lg:text-[32px]">
          {carouselContent.fields.title}
        </H2>
        {carouselContent.fields.link && (
          <ContentfulLink
            content={carouselContent.fields.link}
            className="leading-lg text-brand-primary-black text-xs font-bold underline lg:text-base lg:font-extrabold"
          />
        )}
      </div>
      <div
        className="overflow-x-visible xl:overflow-x-hidden"
        style={
          {
            "--swiper-number-of-columns-desktop":
              carouselContent.fields.numberOfColumnsDesktop,
            "--swiper-number-of-columns-tablet":
              carouselContent.fields.numberOfColumnsTablet,
            "--swiper-number-of-columns-mobile":
              carouselContent.fields.numberOfColumnsMobile,
            "--swiper-right-margin-mobile":
              CAROUSEL_TILE_RIGHT_MARGIN_MOBILE + "px",
            "--swiper-right-margin-tablet":
              CAROUSEL_TILE_RIGHT_MARGIN_TABLET + "px",
            "--swiper-right-margin-desktop":
              CAROUSEL_TILE_RIGHT_MARGIN_DESKTOP + "px",
          } as React.CSSProperties
        }
      >
        <Carousel
          className={cn(
            "contentful-slider !ml-0 !mr-auto mt-5 !w-[95%]",
            className,
          )}
          slidesPerGroup={1}
          centeredSlides={false}
          useBubblesNavigation
          breakpoints={{
            [sm]: {
              slidesPerView: carouselContent.fields.numberOfColumnsMobile || 1,
              spaceBetween: CAROUSEL_TILE_RIGHT_MARGIN_MOBILE,
            },
            [md]: {
              slidesPerView: carouselContent.fields.numberOfColumnsTablet || 1,
              spaceBetween: CAROUSEL_TILE_RIGHT_MARGIN_TABLET,
            },
            [lg]: {
              slidesPerView: carouselContent.fields.numberOfColumnsDesktop || 1,
              spaceBetween: CAROUSEL_TILE_RIGHT_MARGIN_DESKTOP,
            },
          }}
          modules={[Navigation]}
          slideClassName={cn("w-full [&>div]:h-full [&>div]:w-full")}
          autoHeight={true}
          //hideControlsOnMobile={true}
          //isHeroBanner={false}
          // pagination={{
          //   bulletClass: "swiper-pagination-bullet",
          //   clickable: true,
          // }}
          draggable
        >
          {tiles?.map(
            (tile, key) => tile && getTile(tile, key, numberOfColumns),
          )}
        </Carousel>
      </div>
    </div>
  );
};
